'use client';

import { useState } from 'react';

import { LineArrowDownIcon } from '@/app/_components/icons/line';
import { LineArrowRightIcon } from '@/app/_components/icons/line/LineArrowRightIcon';
import type { _IStickerCard } from '@/app/(football)/[team]/_components/common/StickerCard/_hooks/useStickerCard';
import type { StickerCategories, TeamSlug } from '@/app/(football)/[team]/_context/types';
import type { Step } from '@/app/(football)/[team]/main/_components/Modal/SetDefaultCardModal';
import { cn } from '@/app/lib/utils/tailwindcss';
import { trackEvent } from '@/libs/analytics/events';
import type { PackName } from '@/store/server/common/digital-card/_constant';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';

import MyCardList from './MyCardList';
import StickerList from './StickerList';
import Trapezoid from './Trapezoid';

const TOGGLE_BUTTON_HEIGHT = 20;
const SHEET_CONTENT_MIN_HEIGHT = 208;

interface BottomSheetProps {
  step: Step;
  setStep: (step: Step) => void;
  packName: string;
  originalsName: OriginalsName;
  initialPackName?: string;
  initialOriginalsName?: OriginalsName;
  stickerCard: _IStickerCard | null;
  onSelectCard: (metadataIdx: number, cardName: string, image: string) => void;
  onAddSticker: (idx: string, src: string) => void;
  onSelectSticker: (idx: string) => void;
  teamSlug: TeamSlug;
  stickerCategories: StickerCategories;
  history: Record<number, { packName: PackName; originalsName: OriginalsName }> | undefined;
}

export default function BottomSheet({
  step,
  setStep,
  packName,
  originalsName,
  initialPackName,
  initialOriginalsName,
  stickerCard,
  onSelectCard,
  onAddSticker,
  onSelectSticker,
  teamSlug,
  stickerCategories,
  history,
}: BottomSheetProps) {
  const [isOpen, setIsOpen] = useState(true);

  const isSelectCardStep = step === 'select-card';

  const handleNext = () => {
    if (stickerCard) {
      trackEvent({ name: 'next_rep_card', params: { rep_card: stickerCard.cardName, card_idx: stickerCard.metadataIdx } });
      setStep('select-sticker');
    }
  };

  return (
    <div className={cn('fixed inset-x-0 bottom-0 z-1200 mx-auto w-screen max-w-[480px]')}>
      {/* SheetActionContainer */}
      <div className="relative w-full">
        {/* StepButtonWrapper */}
        {isSelectCardStep && stickerCard && (
          <div className="absolute right-5" style={{ top: -TOGGLE_BUTTON_HEIGHT * 2 }}>
            <button
              type="button"
              onClick={handleNext}
              className="flex w-[74px] cursor-pointer items-center justify-between gap-0.5 rounded-3xl bg-card-deco-button-bg px-3 py-2 pl-4 typo-body-1 text-white"
            >
              다음
              <LineArrowRightIcon size={16} color="white" />
            </button>
          </div>
        )}

        {/* SheetTopEdge: 사다리꼴 + 양옆 보더를 하나로 통합 */}
        <div className="flex w-full items-end">
          <div className="h-px flex-1 bg-[var(--color-card-deco-sheet-border)]" />
          <button
            type="button"
            aria-label="Sheet Toggle Button"
            onClick={() => setIsOpen((prev) => !prev)}
            className="relative flex cursor-pointer items-center justify-center bg-transparent"
            style={{ width: 60, height: TOGGLE_BUTTON_HEIGHT }}
          >
            <Trapezoid />
            <div
              className="absolute top-1 transition-transform duration-250 ease-linear"
              style={{ transform: isOpen ? undefined : 'rotate(180deg)' }}
            >
              <LineArrowDownIcon color="var(--color-card-deco-arrow-down-stroke)" size={16} />
            </div>
          </button>
          <div className="h-px flex-1 bg-[var(--color-card-deco-sheet-border)]" />
        </div>
      </div>

      {/* SheetArea */}
      <div
        className="flex w-full items-center justify-center overflow-hidden overflow-x-auto bg-card-deco-sheet-bg transition-[height] duration-250 ease-linear pb-5"
        style={{
          height: isOpen ? 'auto' : 24,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {/* ListWrapper */}
        <div className="size-full transition-opacity duration-250 ease-linear" style={{ opacity: isOpen ? 1 : 0, minHeight: SHEET_CONTENT_MIN_HEIGHT }}>
          {isSelectCardStep ? (
            <MyCardList originalsName={originalsName} packName={packName} initialPackName={initialPackName} initialOriginalsName={initialOriginalsName} selectedStickerCard={stickerCard} onSelectCard={onSelectCard} teamSlug={teamSlug} history={history} />
          ) : (
            <StickerList
              originalsName={originalsName}
              stickerCard={stickerCard}
              onAddSticker={onAddSticker}
              onSelectSticker={onSelectSticker}
              onStepToSelectCard={() => setStep('select-card')}
              teamSlug={teamSlug}
              stickerCategories={stickerCategories}
            />
          )}
        </div>
      </div>
    </div>
  );
}
